/* eslint-disable react/prop-types */
import React, { useState } from 'react';

import {
  CapsuleStat,
  DistributionDonut,
  FilterDropdown,
  FunnelBars,
  SectionCard,
} from '../../../ui';
import { CHART_SEMANTIC, CHART_SERIES } from '../../../ui/chartPalette';
import styles from '../board.less';

const WEEK_COLORS = {
  未训练: CHART_SEMANTIC.danger,
  训练1次: CHART_SEMANTIC.warning,
  '训练次数≥2': CHART_SEMANTIC.success,
};

const DISTRIBUTION_COLORS = {
  月卡: CHART_SERIES[0],
  年卡: CHART_SERIES[6],
  PRO: CHART_SERIES[4],
  自动续费: CHART_SERIES[2],
};

/**
 * 1. 会员概览
 * ------------------------------------------------------------------
 * 本 pane 不做任何跳转（用户要求下掉内联跳转与跳转提示，原 useMemberJump 已删）。
 */
export const OverviewPane = ({ data, enums, loading, error, onRetry }) => {
  const [range, setRange] = useState('all');
  const funnel = (data.funnel || {})[range] || [];
  const base = funnel.length ? Number(funnel[0].num) || 1 : 1;
  /* 圆环百分比是**相对总会员数**（商家端 85.2% / 78.9% / 27.0% / 7.0%），
     不是相对上一级——这点和经营概览那条漏斗的括号语义不同。
     ⚠ label 必须用 s.stage：真实接口的 ringLabel 就是转化率（如 '21.0%'），
     拿它当标签会和上面的 percent 显示成同一个数（线上就是这样，阶段名反而看不见）。 */
  const rings = funnel.slice(1).map((s) => ({
    key: s.stage,
    label: s.stage,
    percent: `${(((Number(s.num) || 0) / base) * 100).toFixed(1)}%`,
    advice: s.advice,
    adviceList: s.adviceList || [],
  }));
  const [activeRing, setActiveRing] = useState(0);
  const ring = rings[activeRing];

  return (
    <>
      {/* 概览胶囊 / 在期分布 / 最近7天完课 并排一行；
          漏斗独占一行——它下面要放四个圆环，需要整行宽度 */}
      <div className={styles.chartRow3Wrap}>
        <SectionCard
          title="会员概览"
          loading={loading}
          error={error}
          onRetry={onRetry}
        >
          <div className={styles.capsuleStack}>
            <CapsuleStat
              label="全部会员（在期）"
              value={data.effectiveUserNum}
              tone="primary"
            />
            <CapsuleStat
              label="流失会员"
              value={data.lostUserNum}
              tone="danger"
            />
          </div>
        </SectionCard>

        <SectionCard
          title="在期会员分布"
          loading={loading}
          error={error}
          onRetry={onRetry}
        >
          <DistributionDonut
            data={(data.distribution || []).map((i) => ({
              name: i.name,
              num: i.num,
              memberType: i.memberType,
            }))}
            colors={DISTRIBUTION_COLORS}
            height={200}
          />
        </SectionCard>

        <SectionCard
          title="最近 7 天完课情况"
          loading={loading}
          error={error}
          onRetry={onRetry}
        >
          <DistributionDonut
            data={data.weekCompletePie || []}
            colors={WEEK_COLORS}
            height={200}
          />
        </SectionCard>
      </div>

      <SectionCard
        title="会员转化漏斗"
        loading={loading}
        error={error}
        onRetry={onRetry}
        extra={
          <FilterDropdown
            value={range}
            options={enums.funnelRange || []}
            onChange={setRange}
          />
        }
      >
        <FunnelBars
          data={funnel.map((s) => ({
            label: s.stage,
            value: s.num,
            unit: '人',
            tip: s.tip,
          }))}
          showRate={false}
        />

        {/* 圆环在漏斗**下方一排**（与商家端一致）。之前它渲染成带边框的裸文字
            堆在一起，是因为 ringRow / ring 这些类还没写进 board.less，
            不是位置放错——位置本来就对。 */}
        <div className={styles.ringRow}>
          {rings.map((r, i) => (
            <button
              key={r.key}
              type="button"
              className={`${styles.ringItem} ${
                i === activeRing ? styles.ringActive : ''
              }`}
              onClick={() => setActiveRing(i)}
            >
              <span className={styles.ring}>{r.percent}</span>
              <span className={styles.ringLabel}>{r.label}</span>
            </button>
          ))}
        </div>

        {/* 建议文案：优先用服务端逐行版（h5 的 ⅰ 提示就是 dataExplainList 逐行渲染，
            每行带自己的 color）；只有服务端只给了拍平字符串时才退成单段。
            注意源字段是 Explain.dataExplainList，不是原型里那个不存在的
            {desc,benchmark,action,jumpLabel} 对象 —— 按那个取会渲染出空行。 */}
        {ring && (ring.adviceList.length > 0 || ring.advice) && (
          <div className={styles.advice} style={{ '--ring-index': activeRing }}>
            {ring.adviceList.length > 0 ? (
              ring.adviceList.map((line, i) => (
                <p
                  key={`${i}-${line.content}`}
                  style={line.color ? { color: line.color } : undefined}
                >
                  {line.content}
                </p>
              ))
            ) : (
              <p>{ring.advice}</p>
            )}
          </div>
        )}
      </SectionCard>
    </>
  );
};

export default OverviewPane;
